import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { usePrevIfUndefined } from "../../../hooks/usePrevIfUndefined";
import { useLayerItemFetchEffect } from "./useLayerItemFetchEffect";
import { useGeometrySelection } from "../../../hooks/geometrySelection/useGeometrySelection";
import { useGeometrySelectionConditions } from "../../../hooks/geometrySelection/useGeometrySelectionConditions";
import { combineLayers, useProjectLayers } from "../../../hooks/project/useProjectLayers";
import { PostgresLayerWithTable, usePostgresLayerWithTable } from "../../../hooks/layer/usePostgresLayerWithTable";
import { addLayerCopyright } from "../../../ducks/layerCopyrights/layerCopyrights";
import { featuresCount } from "../../../resources/features";

import { FilterConditionOperation } from "../../../components/LayerFilter/types";

export type LayerItemInfo = Omit<PostgresLayerWithTable, "rawConfiguration">;

// eslint-disable-next-line max-lines-per-function
export const useLayerItem = (layerName: string): LayerItemInfo => {
  const dispatch = useDispatch();
  const projectLayers = useProjectLayers();
  const { table, layer, segmentStyle, status, refetch, loading } = usePostgresLayerWithTable(layerName);
  const [{ active, type, history, settings }] = useGeometrySelection();
  const geometrySelectionConditions = useGeometrySelectionConditions();
  const [lastCount, setLastCount] = useState<number | null>(null);
  const [count, setCount] = useState<number | null>(null);

  const isLayerVisible = useMemo(
    () => combineLayers(projectLayers)?.find(item => item.name === layerName)?.isVisible,
    [layerName, projectLayers],
  );

  const condition = useMemo(
    () =>
      [layer.condition, geometrySelectionConditions?.[layerName]]
        .filter(Boolean)
        .join(` ${FilterConditionOperation.AND} `),
    [geometrySelectionConditions, layer.condition, layerName],
  );

  const { data: featuresCountData, refetch: refetchFeaturesCount } = featuresCount.use({
    input: { name: layerName, condition },
    immediate: false,
  });

  const layerData = usePrevIfUndefined(layer.name || status ? layer : undefined);
  const tableData = usePrevIfUndefined(table.name || status ? table : undefined);

  useEffect(() => {
    if (layer.copyrightText) {
      dispatch(addLayerCopyright(layer.copyrightText));
    }
  }, [dispatch, layer.copyrightText]);

  useEffect(() => {
    if (featuresCountData === undefined) {
      return;
    }

    setCount(featuresCountData);
  }, [featuresCountData]);

  useEffect(() => {
    if (!active) {
      setLastCount(null);
      setCount(null);
    }
  }, [active]);

  useEffect(() => {
    if (!active || !isLayerVisible) {
      return;
    }

    setLastCount(count);

    if (history.step > -1 && history.features[history.step].length > 0) {
      refetchFeaturesCount();
    } else {
      setCount(null);
    }
  }, [active, type, history.features, history.step, settings, isLayerVisible]);

  useLayerItemFetchEffect({
    layerName,
    layer,
    status,
    lastCount,
    count,
    isVisible: isLayerVisible,
    geometryAttribute: layer.layerDefinition?.geometryAttribute,
  });

  return useMemo(
    () => ({
      layer: layerData || {},
      table: tableData || {},
      segmentStyle,
      status,
      refetch,
      loading: (!layerData || !tableData) && loading,
    }),
    [layerData, segmentStyle, tableData, status, refetch, loading],
  );
};
